import { CaseSensitive, ChevronDown, ChevronRight, Regex, Replace, ReplaceAll, WholeWord, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Spinner } from "../components/ui";
import type { ReviewUser } from "../editor/collab";
import { api, errorMessage, type SearchMatch, type SearchResults } from "../lib/api";
import { toast, useLocalStorage } from "../lib/app";
import { buildMatcher, replaceInText, type MatchOptions } from "./replace";
import type { ProjectSession } from "./session";

interface Props {
  projectId: string;
  session: ProjectSession;
  canEdit: boolean;
  /** Record replacements as this person's suggestions (track changes is on). */
  trackAs: ReviewUser | null;
  /** Text to search for, e.g. the editor's selection when the panel was opened; n changes on every open. */
  seed: { text: string; n: number };
  onOpen: (path: string, jump: { line: number; column: number; length: number }) => void;
  onClose: () => void;
}

interface Target {
  docId: string;
  path: string;
  match?: SearchMatch;
}

const SYNC_TIMEOUT_MS = 15_000;
const LEAD = 12;

/** Search (and replace) across every file in the project. */
export function SearchPanel({ projectId, session, canEdit, trackAs, seed, onOpen, onClose }: Props) {
  const [query, setQuery] = useState(seed.text);
  const [replacement, setReplacement] = useState("");
  const [showReplace, setShowReplace] = useLocalStorage("benleaf:search:replace", false);
  const [opts, setOpts] = useLocalStorage<MatchOptions>("benleaf:search:opts", { regex: false, caseSensitive: false, wholeWord: false });
  const [results, setResults] = useState<SearchResults | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [picked, setPicked] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const token = useRef(0);

  useEffect(() => {
    if (seed.text) setQuery(seed.text);
    input.current?.focus({ preventScroll: true });
    input.current?.select();
  }, [seed.n, seed.text]);

  useEffect(() => {
    const t = ++token.current;
    if (!query) {
      setResults(null);
      setError("");
      setLoading(false);
      return;
    }
    const timer = window.setTimeout(async () => {
      setLoading(true);
      const params = new URLSearchParams({
        q: query,
        regex: opts.regex ? "1" : "0",
        case: opts.caseSensitive ? "1" : "0",
        word: opts.wholeWord ? "1" : "0",
      });
      try {
        const r = await api.get<SearchResults>(`/api/projects/${projectId}/search?${params}`);
        if (t === token.current) {
          setResults(r);
          setError("");
        }
      } catch (e) {
        if (t === token.current) {
          setResults(null);
          setError(errorMessage(e));
        }
      } finally {
        if (t === token.current) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, opts, projectId, refresh]);

  async function replace(targets: Target[]) {
    let re: RegExp;
    try {
      re = buildMatcher(query, opts);
    } catch (e) {
      toast(errorMessage(e), "error");
      return;
    }
    const byDoc = new Map<string, Target[]>();
    for (const t of targets) byDoc.set(t.docId, [...(byDoc.get(t.docId) ?? []), t]);
    let replaced = 0;
    let files = 0;
    let missed = 0;
    let i = 0;
    for (const [docId, list] of byDoc) {
      setBusy(byDoc.size > 1 ? `Replacing in ${++i} of ${byDoc.size} files…` : "Replacing…");
      // Open it like the editor does, so the change is saved (and kept offline) the same way as typing.
      const h = session.openDoc(docId);
      const synced = await Promise.race([h.synced.then(() => true), new Promise<boolean>((r) => setTimeout(() => r(false), SYNC_TIMEOUT_MS))]);
      if (!synced || h.readOnly) {
        missed += list.length;
        continue;
      }
      let n = 0;
      if (list.some((t) => !t.match)) n = replaceInText(h.ytext, re, replacement, opts.regex, trackAs);
      // Single matches go last to first, so earlier ones keep their line and column.
      else for (const t of [...list].reverse()) n += replaceInText(h.ytext, re, replacement, opts.regex, trackAs, t.match);
      if (list.every((t) => t.match)) missed += list.length - n;
      if (n) {
        replaced += n;
        files++;
      }
    }
    setBusy(null);
    if (replaced) {
      toast(
        `Replaced ${replaced} ${replaced === 1 ? "match" : "matches"}${files > 1 ? ` in ${files} files` : ""}${trackAs ? " as tracked changes" : ""}` +
          (missed ? `. ${missed} had changed since the search and were left alone.` : ""),
      );
    } else toast(missed ? "Those matches changed since the search, so nothing was replaced." : "Nothing to replace.", missed ? "error" : "info");
    setRefresh((r) => r + 1);
  }

  const fileCount = results?.files.length ?? 0;
  const canReplace = canEdit && showReplace && !!query && !busy;
  const replaceAll = () => {
    if (!results) return;
    const what = `${results.total} ${results.total === 1 ? "match" : "matches"}${fileCount > 1 ? ` in ${fileCount} files` : ""}`;
    if (!confirm(`Replace ${what} with “${replacement}”?${trackAs ? " They'll be recorded as tracked changes." : " You can undo this from History."}`)) return;
    replace(results.files.map((f) => ({ docId: f.docId, path: f.path })));
  };
  const toggle = (key: keyof MatchOptions) => setOpts({ ...opts, [key]: !opts[key] });

  return (
    <div className="panel search-panel" style={{ flex: 1 }}>
      <div className="panel-head">
        <span className="title">Search</span>
        <button className="icon-btn" title="Back to files (Esc)" onClick={onClose}>
          <X size={15} />
        </button>
      </div>
      <div className="search-form">
        <div className="search-row">
          {canEdit && (
            <button className="icon-btn search-expand" title={showReplace ? "Hide replace" : "Replace"} onClick={() => setShowReplace(!showReplace)}>
              {showReplace ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          )}
          <div className="search-input">
            <input
              ref={input}
              type="search"
              placeholder="Search all files"
              aria-label="Search all files"
              value={query}
              autoFocus
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.preventDefault();
                  onClose();
                } else if (e.key === "Enter") setRefresh((r) => r + 1);
              }}
            />
            <button className={`icon-btn${opts.caseSensitive ? " on" : ""}`} title="Match case" aria-pressed={opts.caseSensitive} onClick={() => toggle("caseSensitive")}>
              <CaseSensitive size={15} />
            </button>
            <button className={`icon-btn${opts.wholeWord ? " on" : ""}`} title="Whole words" aria-pressed={opts.wholeWord} onClick={() => toggle("wholeWord")}>
              <WholeWord size={15} />
            </button>
            <button className={`icon-btn${opts.regex ? " on" : ""}`} title="Regular expression" aria-pressed={opts.regex} onClick={() => toggle("regex")}>
              <Regex size={15} />
            </button>
          </div>
        </div>
        {canEdit && showReplace && (
          <div className="search-row">
            <span className="search-expand" />
            <div className="search-input">
              <input
                type="text"
                placeholder={opts.regex ? "Replace ($1 for groups)" : "Replace"}
                aria-label="Replace with"
                value={replacement}
                onChange={(e) => setReplacement(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && results?.total && !results.truncated) replaceAll();
                }}
              />
              <button
                className="icon-btn"
                title={results?.truncated ? "Too many matches to replace at once. Narrow the search first." : "Replace all (Ctrl/⌘+Enter)"}
                disabled={!canReplace || !results?.total || results.truncated}
                onClick={replaceAll}
              >
                <ReplaceAll size={15} />
              </button>
            </div>
          </div>
        )}
        <div className="search-summary">
          {busy ? (
            <>
              <Spinner size={12} /> {busy}
            </>
          ) : error ? (
            <span className="error-text">{error}</span>
          ) : loading && !results ? (
            <Spinner size={12} />
          ) : results ? (
            results.total === 0 ? (
              "No results"
            ) : (
              <>
                {results.truncated ? "First " : ""}
                {results.total.toLocaleString()} {results.total === 1 ? "result" : "results"}
                {fileCount > 1 ? ` in ${fileCount} files` : ""}
                {trackAs && showReplace && canEdit ? " · replacing as tracked changes" : ""}
              </>
            )
          ) : null}
        </div>
      </div>
      <div className="panel-body search-results">
        {results?.files.map((f) => {
          const open = !collapsed.has(f.path);
          const slash = f.path.lastIndexOf("/");
          return (
            <div key={f.path} className="search-file">
              <div
                className="search-file-head"
                role="button"
                tabIndex={0}
                onClick={() => {
                  const next = new Set(collapsed);
                  if (open) next.add(f.path);
                  else next.delete(f.path);
                  setCollapsed(next);
                }}
              >
                {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                <span className="search-file-name">
                  <b>{f.path.slice(slash + 1)}</b>
                  {slash > 0 && <span className="muted"> {f.path.slice(0, slash)}</span>}
                </span>
                {canReplace && (
                  <button
                    className="icon-btn"
                    title="Replace all in this file"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (f.matches.length > 1 && !confirm(`Replace ${f.matches.length} matches in ${f.path} with “${replacement}”?`)) return;
                      replace([{ docId: f.docId, path: f.path }]);
                    }}
                  >
                    <ReplaceAll size={13} />
                  </button>
                )}
                <span className="badge">{f.matches.length}</span>
              </div>
              {open &&
                f.matches.map((m) => {
                  const key = `${f.path}:${m.line}:${m.column}`;
                  return (
                    <div
                      key={key}
                      className={`search-hit${picked === key ? " active" : ""}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setPicked(key);
                        onOpen(f.path, { line: m.line, column: m.column, length: m.length });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") (e.currentTarget as HTMLElement).click();
                      }}
                    >
                      <span className="search-line">{m.line}</span>
                      <span className="search-preview">
                        {/* Only a little leading context, so the match itself stays visible in a narrow sidebar. */}
                        {m.before.length > LEAD ? `…${m.before.slice(-LEAD).trimStart()}` : m.before}
                        <mark>{m.text}</mark>
                        {m.after}
                      </span>
                      {canReplace && (
                        <button
                          className="icon-btn"
                          title="Replace"
                          onClick={(e) => {
                            e.stopPropagation();
                            replace([{ docId: f.docId, path: f.path, match: m }]);
                          }}
                        >
                          <Replace size={13} />
                        </button>
                      )}
                    </div>
                  );
                })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
